import { DateTime } from "luxon";
import {
  Constraint,
  Identifiable,
  OptimizedGantt,
  ProjectElementType,
} from "@planzone/dto";

function isBlocked(
  element: OptimizedGantt[number],
  constraints: (Identifiable<Constraint> & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  })[],
) {
  return !!constraints.find(
    ({ targetEntityUuid, originDateType, targetDateType, originIsDone }) =>
      targetEntityUuid === element.uuid &&
      originDateType === "END" &&
      targetDateType === "START" &&
      !originIsDone,
  );
}

function isProject({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.PROJECT;
}

function isList({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.LIST;
}

function isTask({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.TASK;
}

function isMilestone({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.MILESTONE;
}

function isEvent({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.EVENT;
}

function isTaskDone(
  projectElement: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.TASK }
  >,
): boolean {
  return (
    projectElement.status.percentComplete === 100 ||
    projectElement.percentComplete === 100 ||
    (projectElement.allocations.length > 0 &&
      projectElement.allocations.every(
        ({ percentComplete }) => percentComplete === 100,
      ))
  );
}

function isMilestoneDone(
  projectElement: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.MILESTONE }
  >,
): boolean {
  return projectElement.state === "DONE";
}

function isEventDone(
  projectElement: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.EVENT }
  >,
): boolean {
  return (
    !!projectElement.endDate &&
    DateTime.fromISO(projectElement.endDate, { zone: "utc" }) > DateTime.now()
  );
}

function isListDone(
  projectElement: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.LIST }
  >,
): boolean {
  return false;
}

function isProjectDone(
  projectElement: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.PROJECT }
  >,
): boolean {
  return false;
}

function isDone(projectElement: OptimizedGantt[number]) {
  return projectElement.type === ProjectElementType.TASK
    ? isTaskDone(projectElement)
    : projectElement.type === ProjectElementType.MILESTONE
      ? isMilestoneDone(projectElement)
      : projectElement.type === ProjectElementType.EVENT
        ? isEventDone(projectElement)
        : projectElement.type === ProjectElementType.LIST
          ? isListDone(projectElement)
          : isProjectDone(projectElement);
}

export { isBlocked, isProject, isList, isTask, isMilestone, isEvent, isDone };
